import { makeAutoObservable, action, extendObservable, set } from 'mobx';
import dayjs from 'dayjs';
import { getInitialParams, saveParamsToStorage } from './common/filterUtils';
import domainData from 'store/domainStore';
import dashboardService from 'services/dashboardService';
import logger from 'logger';
import M from 'messages';
import configOptions from '../pages/wines/config/config';

const STORAGE_KEY = 'dashboard_filter_params';

const WINE_COLOR_MAP = {
  Red: '#C70039',
  White: '#FAF3B1',
  Rose: '#FF6F91',
  Orange: '#FFA500',
};

const WINE_SWEETNESS_MAP = {
  'Brut Nature': '#0D3B66',
  'Extra Brut': '#145374',
  Brut: '#1E6F9F',
  'Extra Dry': '#2E8BC0',
  Dry: '#47B5FF',
  'Demi Sec': '#7FBFFF',
  Doux: '#A4D4FF',
  'Semi Dry': '#C0E8FF',
  'Semi Sweet': '#DFF4FF',
  Sweet: '#F0FBFF',
};

const WINE_STYLE_MAP = {
  Still: '#66F2A4',
  Sparkling: '#FFD700',
  Dessert: '#FFA500',
  Fortified: '#FF4500',
};

const parseUTCDate = (dateStr, isStart) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  // Set local time to 00:00:00 or 23:59:59.999
  if (isStart) {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setHours(23, 59, 59, 999);
  }
  // Convert local time to UTC
  return date.toISOString();
};

const defaultFilterParams = {
  params: {
    dateDropdownType: 'last_7_days',
    dateRange: {
      start: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
      end: dayjs().format('YYYY-MM-DD'),
    },
    selectedMenuId: '',
    currentSPId: '',
  },
};
class DashboardStore {
  filterData = defaultFilterParams;
  getDefaultStoreProps = () => ({
    loading: false,
    KPIGlobalDataLoading: true,
    KPIDataLoading: true,
    viewsByColorLoading: true,
    averageRatingByWineLoading: true,
    viewsBySweetnessLoading: true,
    engagementOverTimeLoading: true,
    viewsByStyleLoading: true,
    viewsByCountryLoading: true,
    clicksByWineLoading: true,
    menus: [],
    serviceProviders: [],
    totalWines: 0,
    totalFollowers: 0,
    totalVisits: 0,
    totalViews: 0,
    totalRatings: 0,
    averageRating: 0,
    timeTotalRatings: 0,
    timeTotalVisits: 0,
    timeAverageRating: 0,
    visitsByCountry: [],
    viewsByWine: [],
    averageRatingByWine: [],
    viewsByColor: [],
    viewsBySweetness: [],
    engagementOverTime: [],
    viewsByStyle: [],
    viewsByRegion: [],
    clicksByWine: [],
  });

  constructor() {
    makeAutoObservable(this);
    extendObservable(this, { ...this.getDefaultStoreProps() });
  }

  @action
  reset() {
    set(this, this.getDefaultStoreProps());
  }

  @action
  setFilter(filter) {
    this.filterData = filter;
    // Save to localStorage using common utility
    saveParamsToStorage(STORAGE_KEY, filter);
  }

  // Get current filter parameters using common utility with validation
  getCurrentFilterParams = (urlParams = {}) => {
    const isUserSuperAdmin = domainData.userIsSuperAdmin();
    const subscriptionAccess = domainData.getSubscriptionAllAccess();
    const userContext = { isUserSuperAdmin, subscriptionAccess };

    return getInitialParams(STORAGE_KEY, defaultFilterParams, null, userContext, urlParams);
  };

  setServiceProviders = serviceProviders => {
    this.serviceProviders = serviceProviders;
  };

  fetchMenus = async ({ params }) => {
    const { data } = await dashboardService.getMenus(params.currentSPId);
    this.menus = data;
    return data;
  };

  fetchKPIGlobalData = async ({ params }) => {
    try {
      // Fetch data from API (replace with actual API call)
      this.KPIGlobalDataLoading = true;
      const { data } = await dashboardService.getKPIData(params.currentSPId);

      // For now, using hardcoded values
      this.totalWines = data.totalWines; // Update with `data.totalWines`
      this.totalFollowers = data.totalFollowers; // Update with `data.totalFollowers`
      this.totalRatings = data.totalRatings; // Update with `data.totalRatings`
      this.averageRating = data.averageRating; // Update with `data.averageRating`
      this.totalVisits = data.totalVisits; // Update with `data.totalVisits`
    } catch (error) {
      logger.error('Failed to fetch KPI data', error);
    } finally {
      this.KPIGlobalDataLoading = false;
    }
  };

  // Placeholder function for API call to fetch KPI data
  fetchTimeKPIData = async ({ params }) => {
    try {
      this.KPIDataLoading = true;
      // Fetch data from API (replace with actual API call)
      const { data } = await dashboardService.dashboardTimeKPIs(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );

      this.totalViews = data.totalViews; // Update with `data.totalViews`
      this.timeTotalVisits = data.totalVisits; // Update with `data.totalVisits`
      this.timeTotalRatings = data.totalRatings; // Update with `data.totalRatings`
      this.timeAverageRating = data.averageRating; // Update with `data.averageRating`
      this.viewsByWine = data.viewsByWine; // Update with `data.viewsByWine`
    } catch (error) {
      logger.error('Failed to fetch KPI data', error);
    } finally {
      this.KPIDataLoading = false;
    }
  };

  // Placeholder function for API call to fetch Visits by Country data
  fetchVisitsByWineColors = async ({ params }) => {
    try {
      this.viewsByColorLoading = true;
      const response = await dashboardService.getViewsByColor(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      const data = response.data.viewsByColor;
      for (let i = 0; i < data.length; i += 1) {
        data[i].color = WINE_COLOR_MAP[data[i].name];
        data[i].name = M.get(configOptions.colorGroup.fields.filter(item => item.value === data[i].name)[0].label);
      }
      this.viewsByColor = data;
    } catch (error) {
      logger.error('Failed to fetch visits by country data', error);
    } finally {
      this.viewsByColorLoading = false;
    }
  };

  // Placeholder function for API call to fetch Average Rating by Wine data
  fetchAverageRatingByWine = async ({ params }) => {
    try {
      this.averageRatingByWineLoading = true;
      const response = await dashboardService.getAverageRatingByWine(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      this.averageRatingByWine = response.data.averageRatingByWine;
    } catch (error) {
      logger.error('Failed to fetch average rating by wine data', error);
    } finally {
      this.averageRatingByWineLoading = false;
    }
  };

  // Placeholder function for API call to fetch Views by Sweetness data
  fetchViewsByWineSweetness = async ({ params }) => {
    try {
      this.viewsBySweetnessLoading = true;
      const response = await dashboardService.getViewsBySweetness(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      const data = response.data.viewsBySweetness;
      for (let i = 0; i < data.length; i += 1) {
        data[i].color = WINE_SWEETNESS_MAP[data[i].name];
        data[i].name = M.get(configOptions.sweetnessGroup.fields.filter(item => item.value === data[i].name)[0].label);
      }
      this.viewsBySweetness = data;
    } catch (error) {
      logger.error('Failed to fetch views by sweetness data', error);
    } finally {
      this.viewsBySweetnessLoading = false;
    }
  };

  fetchEngagementOverTime = async ({ params }) => {
    try {
      this.engagementOverTimeLoading = true;
      const response = await dashboardService.getEngagementOverTime(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      this.engagementOverTime = response.data.engagementOverTime;
    } catch (error) {
      logger.error('Failed to fetch engagement over time data', error);
    } finally {
      this.engagementOverTimeLoading = false;
    }
  };

  // Placeholder function for API call to fetch Views by Style data
  fetchViewsByWineStyle = async ({ params }) => {
    try {
      this.viewsByStyleLoading = true;
      const response = await dashboardService.getViewsByStyle(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      const data = response.data.viewsByStyle;
      for (let i = 0; i < data.length; i += 1) {
        data[i].color = WINE_STYLE_MAP[data[i].name];
        data[i].name = M.get(configOptions.styleGroup.fields.filter(item => item.value === data[i].name)[0].label);
      }
      this.viewsByStyle = data;
    } catch (error) {
      logger.error('Failed to fetch views by style data', error);
    } finally {
      this.viewsByStyleLoading = false;
    }
  };

  // Placeholder function for API call to fetch Views by Style data
  fetchViewsByCountry = async ({ params }) => {
    try {
      this.viewsByCountryLoading = true;
      const response = await dashboardService.getViewsByRegion(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      this.viewsByRegion = response.data.viewsByRegion;
    } catch (error) {
      logger.error('Failed to fetch views by style data', error);
    } finally {
      this.viewsByCountryLoading = false;
    }
  };

  // Placeholder function for API call to fetch Clicks by Wine data
  fetchClicksByWine = async ({ params }) => {
    try {
      this.clicksByWineLoading = true;
      const response = await dashboardService.getClicksByWine(
        parseUTCDate(params.dateRange.start, true),
        parseUTCDate(params.dateRange.end, false),
        params.selectedMenuId,
        params.currentSPId,
      );
      this.clicksByWine = response.data.clicksByWine;
    } catch (error) {
      logger.error('Failed to fetch clicks by wine data', error);
    } finally {
      this.clicksByWineLoading = false;
    }
  };
}

const dashboardStore = new DashboardStore();
export default dashboardStore;
